import { Box, Button, Card, SvgIcon, Tooltip } from '@mui/material';
import { GridColDef } from '@mui/x-data-grid';
import { RiAddLine } from '@remixicon/react';
import { useQuery } from '@tanstack/react-query';
import dayjs from 'dayjs';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { MainSectionTemplate } from '../../../components/AdminTemplates';
import CustomDataGrid from '../../../components/CustomDataGrid';
import { useAppDataContext } from '../../../contexts/appData';
import { useCurrentUser } from '../../../contexts/currentUser';
import { useIotsApi } from '../../../contexts/iotsApi';
import { useLanguage } from '../../../contexts/language';
import useQueryErrorNotifier from '../../../hooks/useQueryErrorNotifier';
import { ContractListItem } from '../../../types/contract';
import AdminTableDetailLink from '../../../components/AdminTableDetailLink';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { getGridDateColDef } from '../../../components/CustomDataGrid/utils';
import { useDataGridSearchParamsFiltersManager } from '../../../hooks/useSearchParamsFilters';
import ContractBadge from './ContractBadge';
import { ContractBadgeStatus } from './types';
import { getContractBadgeStatus } from './utils';

export const ContractsPage = () => {
  const { t } = useLanguage();
  const { getContracts } = useIotsApi();
  const { appData } = useAppDataContext();
  const { isOperator } = useCurrentUser();

  const {
    data: contracts = [],
    isFetching,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['contracts'],
    queryFn: () => getContracts(),
  });

  const expiredContractsSet = useMemo<Set<string>>(() => {
    const expiredContractsSet = new Set<string>();
    const today = dayjs();
    contracts.forEach(contract => {
      if (today.isAfter(dayjs(contract.endDate), 'day')) {
        expiredContractsSet.add(contract.id);
      }
    });

    return expiredContractsSet;
  }, [contracts]);

  useQueryErrorNotifier([error]);

  const { initialFilterModel, onFilterModelChange } =
    useDataGridSearchParamsFiltersManager();

  const columns = useMemo<GridColDef<ContractListItem>[]>(
    () => [
      {
        field: 'name',
        flex: 3,
        headerName: t('content.contract-name'),
        minWidth: 220,
      },
      ...(!isOperator
        ? [
            {
              field: 'operatorName',
              flex: 3,
              headerName: t('content.operator'),
              minWidth: 220,
            },
          ]
        : []),
      {
        ...getGridDateColDef(),
        field: 'startDate',
        headerName: t('content.start-date'),
        minWidth: 140,
      },
      {
        ...getGridDateColDef(),
        field: 'endDate',
        headerName: t('content.end-date'),
        minWidth: 140,
      },
      {
        field: 'timetableRoutesCount',
        type: 'number',
        headerName: t('content.timetable-routes'),
        flex: 2,
        minWidth: 140,
        cellClassName: params => (!params.value ? 'bg-iots-red-500/10' : ''),
        renderCell: params => (
          <Tooltip
            key={`vessels-tooltip-${params.id}`}
            title={t('validation.timetable-routes-minimum')}
            disableHoverListener={Boolean(params.value)}
          >
            <Box>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: 'assignedVesselsCount',
        type: 'number',
        headerName: t('content.assigned-vessels'),
        flex: 2,
        minWidth: 140,
        cellClassName: params => (!params.value ? 'bg-iots-red-500/10' : ''),
        renderCell: params => (
          <Tooltip
            key={`vessels-tooltip-${params.id}`}
            title={t('validation.assigned-vessels-minimum')}
            disableHoverListener={Boolean(params.value)}
          >
            <Box>{params.value}</Box>
          </Tooltip>
        ),
      },
      {
        field: 'county',
        flex: 3,
        headerName: t('content.county'),
        minWidth: 160,
        type: 'singleSelect',
        valueOptions: appData.counties.map(county => ({
          value: county,
          label: t(`counties.${county}`),
        })),
      },
      {
        field: 'status',
        flex: 1,
        headerName: t('content.status'),
        display: 'flex',
        minWidth: 160,
        type: 'singleSelect',
        valueOptions: [
          {
            value: ContractBadgeStatus.Compliant,
            label: t(`content.compliant`),
          },
          {
            value: ContractBadgeStatus.Uncompliant,
            label: t('content.uncompliant'),
          },
          { value: ContractBadgeStatus.Expired, label: t(`content.expired`) },
        ],
        valueGetter: (_, row) =>
          getContractBadgeStatus(
            expiredContractsSet.has(row.id),
            row.assignedVesselsCount,
            row.isValidInsurance,
          ),
        renderCell: params => {
          return (
            <ContractBadge status={params.value as ContractBadgeStatus} small />
          );
        },
      },
      {
        field: '',
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        resizable: false,
        hideable: false,
        display: 'flex',
        renderCell: params => (
          <AdminTableDetailLink
            isLoading={isFetching}
            href={`/admin/contracts/${params.id}`}
          />
        ),
      },
    ],
    [isOperator, t, appData, expiredContractsSet, isFetching],
  );

  return (
    <MainSectionTemplate
      title={t('content.contracts')}
      isLoading={isFetching}
      actions={
        !isOperator && (
          <Link to="/admin/contracts/create">
            <Button
              variant="contained"
              startIcon={<SvgIcon component={RiAddLine} />}
              size="small"
            >
              {t('content.add-new')}
            </Button>
          </Link>
        )
      }
    >
      <LocalizationProvider>
        <Card variant="outlined">
          <CustomDataGrid
            rows={contracts}
            loading={isLoading}
            columns={columns}
            onFilterModelChange={onFilterModelChange}
            initialState={{
              filter: {
                filterModel: initialFilterModel,
              },
              sorting: {
                sortModel: [
                  {
                    field: 'name',
                    sort: 'asc',
                  },
                ],
              },
            }}
          />
        </Card>
      </LocalizationProvider>
    </MainSectionTemplate>
  );
};

export default ContractsPage;
